import { customList } from 'country-codes-list';
import type { UserListParams } from '@app/services/user.types';
import { fn } from '.';
import type {
    LocationListParams,
    ContactListParams,
    RoleListParams,
    InteractionListParams,
    InteractionTypeListParams,
    BillingReportsParams,
    AIAgentListParams,
    AIAgentTypeListParams,
    AINumberListParams,
    TokenUsageParams,
} from '@app/services/api.types';
import type { AIFlowListParams } from '@app/services/aiFlow.types';

export const siteName = import.meta.env.VITE_APP_TITLE; // SET SITE NAME
export const prefix = 'enteka_'; // SET UNIQUE PREFIX FOR THE PROJECT!

/**
 * all the routes need to have prefix "/"
 */
export const url = {
    home: '/',
    sessionExpired: '/session-expired',
    tooManyRequests: '/too-many-requests',

    // account related routes
    account: {
        forgotPassword: '/forgot-password',
        forgotAccount: '/forgot-account',
        completeRegistration: '/complete-registration',
        login: '/login',
        ssoVerify: '/sso-verify',
        resetPassword: '/reset-password',
    },

    // all the admin routes should have dashboard prefix
    adminPortal: {
        dashboard: '/dashboard',
        interactions: '/dashboard/interactions',
        contacts: '/dashboard/contacts',
        locations: '/dashboard/locations',
        internalAdmin: {
            base: '/internal-admin',
            clients: {
                list: '/internal-admin/clients',
                add: '/internal-admin/clients/add',
                view: (id: string | number) => `/internal-admin/clients/${id}`,
            },
            channelPartners: {
                list: '/internal-admin/channel-partners',
                add: '/internal-admin/channel-partners/add',
                view: (id: string | number) => `/internal-admin/channel-partners/${id}`,
            },
            brands: {
                list: '/internal-admin/brands',
                add: '/internal-admin/brands/add',
                view: (id: string | number) => `/internal-admin/brands/${id}`,
            },
            locations: {
                list: '/internal-admin/locations',
                add: '/internal-admin/locations/add',
                view: (id: string | number) => `/internal-admin/locations/${id}`,
            },
            assignAgent: {
                list: '/internal-admin/ai-agents',
            },
            billingReports: '/internal-admin/billing-reports',
            tokenUsage: '/internal-admin/token-usage',
            agentFlows: {
                list: '/internal-admin/agent-flows',
                builderAdd: '/internal-admin/agent-flows/builder',
                builderEdit: (id: string | number) => `/internal-admin/agent-flows/builder/${id}`,
            },
        },
        users: {
            list: '/dashboard/users',
            view: (id: string | number) => `/dashboard/users/${id}`,
        },
    },
};

export const userRoles = [
    { id: 'super-admin', role_id: 1, label: 'Super Administrator' },
    { id: 'admin', role_id: 2, label: 'Administrator' },
    { id: 'contributor', role_id: 3, label: 'Contributor' },
];

export const apiEndpoints = {
    auth: {
        login: '/v1/auth/login',
        logout: '/v1/auth/logout',
        me: '/v1/auth/me',
        refreshToken: '/v1/auth/refresh',
        resendMfa: '/v1/auth/resend-2fa-code',
        startPasswordReset: '/v1/auth/start-password-reset',
        setNewPassword: '/v1/auth/set-new-password',
    },
    meta: {
        config: '/v1/meta/config',
        connectWaba: '/v1/meta/connect-waba',
    },
    dashboard: {
        getMyDashboard: '/v1/user/dashboard',
        saveMyDashboard: '/v1/user/dashboard', // payload - layout
        groupStats: '/v1/analytics/interactions/grouped',
        dailyStats: '/v1/analytics/interactions/daily',
        grouped: '/v1/analytics/bookings/grouped',
        byBrand: '/v1/analytics/bookings/by-brand',
        byLocation: '/v1/analytics/bookings/by-location',
    },
    users: {
        getList: (params?: UserListParams) => {
            return fn.buildUrlQuery('/v1/users', params);
        },
        getListExport: (params?: UserListParams) => {
            const exportParams = {
                ...params,
                export: 1,
                page: undefined,
                per_page: undefined,
            };
            return fn.buildUrlQuery('/v1/users', exportParams);
        },
        getSingle: (id: string | number) => `/v1/users/${id}`,
        update: (id: string | number) => `/v1/users/${id}`,
        delete: (id: string | number) => `/v1/users/${id}`,
        add: '/v1/users',
        switch: (id: string) => `/v1/users/${id}/switch`,
        switchBack: `/v1/users/switch-back`,
    },
    interactions: {
        getList: (params?: InteractionListParams) => {
            return fn.buildUrlQuery('/v1/interactions', params);
        },
        getListExport: (params?: InteractionListParams) => {
            const exportParams = {
                ...params,
                export: 1,
                page: undefined,
                per_page: undefined,
            };
            return fn.buildUrlQuery('/v1/interactions', exportParams);
        },
        getSingle: (id: string | number) => `/v1/interactions/${id}`,
        update: (id: string | number) => `/v1/interactions/${id}`,
        delete: (id: string | number) => `/v1/interactions/${id}`,
        add: '/v1/interactions',
    },
    recordings: {
        getSingle: (id: string | number) => `/v1/interactions/${id}/recording`,
    },
    interactionTypes: {
        getList: (params?: InteractionTypeListParams) => {
            return fn.buildUrlQuery('/v1/interaction-types', params);
        },
        getSingle: (id: string | number) => `/v1/interaction-types/${id}`,
    },
    contacts: {
        getList: (params?: ContactListParams) => {
            return fn.buildUrlQuery('/v1/contacts', params);
        },
        getListExport: (params?: ContactListParams) => {
            const exportParams = {
                ...params,
                export: 1,
                page: undefined,
                per_page: undefined,
            };
            return fn.buildUrlQuery('/v1/contacts', exportParams);
        },
        getSingle: (id: string | number) => `/v1/contacts/${id}`,
        update: (id: string | number) => `/v1/contacts/${id}`,
        delete: (id: string | number) => `/v1/contacts/${id}`,
        add: '/v1/contacts',
    },
    brands: {
        getList: (params?: ContactListParams) => {
            return fn.buildUrlQuery('/v1/brands', params);
        },
        getListExport: (params?: UserListParams) => {
            const exportParams = {
                ...params,
                export: 1,
                page: undefined,
                per_page: undefined,
            };
            return fn.buildUrlQuery('/v1/brands', exportParams);
        },
        getSingle: (id: string | number) => `/v1/brands/${id}`,
        update: (id: string | number) => `/v1/brands/${id}`,
        delete: (id: string | number) => `/v1/brands/${id}`,
        add: '/v1/brands',
    },
    role: {
        getList: (params?: RoleListParams) => {
            return fn.buildUrlQuery('/v1/roles', params);
        },
    },
    channel: {
        getList: (params?: ContactListParams) => {
            return fn.buildUrlQuery('/v1/channels', params);
        },
        getListExport: (params?: UserListParams) => {
            const exportParams = {
                ...params,
                export: 1,
                page: undefined,
                per_page: undefined,
            };
            return fn.buildUrlQuery('/v1/channels', exportParams);
        },
        getSingle: (id: string | number) => `/v1/channels/${id}`,
        update: (id: string | number) => `/v1/channels/${id}`,
        delete: (id: string | number) => `/v1/channels/${id}`,
        add: '/v1/channels',
    },
    locations: {
        getList: (params?: LocationListParams) => {
            return fn.buildUrlQuery('/v1/locations', params);
        },
        getListExport: (params?: LocationListParams) => {
            const exportParams = {
                ...params,
                export: 1,
                page: undefined,
                per_page: undefined,
            };
            return fn.buildUrlQuery('/v1/locations', exportParams);
        },
        getSingle: (id: string | number) => `/v1/locations/${id}`,
        update: (id: string | number) => `/v1/locations/${id}`,
        delete: (id: string | number) => `/v1/locations/${id}`,
        add: '/v1/locations',
    },
    aiNumbers: {
        getList: (params?: AINumberListParams) => {
            return fn.buildUrlQuery('/v1/ai-numbers', params);
        },
        getListExport: (params?: AINumberListParams) => {
            const exportParams = {
                ...params,
                export: 1,
                page: undefined,
                per_page: undefined,
            };
            return fn.buildUrlQuery('/v1/ai-numbers', exportParams);
        },
        getSingle: (id: string | number) => `/v1/ai-numbers/${id}`,
        update: (id: string | number) => `/v1/ai-numbers/${id}`,
        delete: (id: string | number) => `/v1/ai-numbers/${id}`,
        add: '/v1/ai-numbers',
    },
    aiAgents: {
        getList: (params?: AIAgentListParams) => {
            return fn.buildUrlQuery('/v1/ai-agents', params);
        },
        getListExport: (params?: AIAgentListParams) => {
            const exportParams = {
                ...params,
                export: 1,
                page: undefined,
                per_page: undefined,
            };
            return fn.buildUrlQuery('/v1/ai-agents', exportParams);
        },
        getSingle: (id: string | number) => `/v1/ai-agents/${id}`,
        update: (id: string | number) => `/v1/ai-agents/${id}`,
        delete: (id: string | number) => `/v1/ai-agents/${id}`,
        add: '/v1/ai-agents',
        assignNumber: '/v1/ai-agents/number',
    },
    aiAgentTypes: {
        getList: (params?: AIAgentTypeListParams) => {
            return fn.buildUrlQuery('/v1/ai-agent-types', params);
        },
    },
    billingReports: {
        getReport: (params: BillingReportsParams) => {
            return fn.buildUrlQuery('/v1/reports/usage', params);
        },
    },
    tokenUsage: {
        getReport: (params?: TokenUsageParams) => {
            return fn.buildUrlQuery('/v1/reports/token-usage', params);
        },
    },
    aiFlows: {
        getList: (params?: AIFlowListParams) => {
            return fn.buildUrlQuery('/v1/flows', params);
        },
        getSingle: (id: string | number) => `/v1/flows/${id}`,
        update: (id: string | number) => `/v1/flows/${id}`,
        delete: (id: string | number) => `/v1/flows/${id}`,
        add: '/v1/flows',
    },
};

const getApiEndpoint = () => {
    return import.meta.env.VITE_API_URL;
};

export const apiEndpoint = getApiEndpoint();

export const defaultErrorMessage = 'Something went wrong, please try again later.';

export const themeColourAttributes = {
    default: 'default',
    primary: 'primary',
    danger: 'danger',
    brand: 'brand',
    green: 'green',
    blue: 'blue',
    orange: 'orange',
    red: 'red',
    purple: 'purple',
    yellow: 'yellow',
    black: 'black',
    white: 'white',
};

export const interactionTypeColours = {
    total: '#3b82f6',
    call: '#10b981',
    email: '#f59e0b',
    whatsapp: '#06b6d4',
    livechat: '#8b5cf6',
};

export const countryOptions = Object.entries(customList('countryCode', '{countryNameEn}')).map(
    ([code, name]) => ({
        label: name,
        value: code,
    })
);
